import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bug, BarChart3, Droplets, Users, Sparkles, Settings } from "lucide-react";
import { GreenhouseIcon } from "@/components/icons/GreenhouseIcon";
import { useAuth } from "@/contexts/AuthContext";
import { useManagerPermissions, MANAGEABLE_SECTIONS } from "@/hooks/useManagerPermissions";
import { cn } from "@/lib/utils";

interface Item {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  color: string;
  adminOnly?: boolean;
  /** Shown on mobile but hidden on tablet, where it already lives in the dock. */
  tabletHidden?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MoreSheet({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useAuth();
  const { isSectionVisible } = useManagerPermissions();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // Order matches the mobile menu; Plagas + Analíticas are hidden on tablet
  // (they sit directly in the tablet dock), which collapses the list to the
  // tablet order: Mapa · Registros · Equipo · Ulture Assistant · Ajustes.
  // Centro de Ayuda is deliberately absent: it now lives inside Ajustes.
  const items: Item[] = [
    { key: "blocks", label: t("nav.blockMap"), icon: GreenhouseIcon, to: "/blocks", color: "#22A565" },
    // Campañas sale de «Más»: ahora es una subpágina de Ajustes (2026-08-11).
    { key: "pests", label: t("nav.pests"), icon: Bug, to: "/pests", color: "#E0962E", tabletHidden: true },
    { key: "spray", label: t("nav.sprayLogs"), icon: Droplets, to: "/spray-logs", color: "#2B7DE0" },
    { key: "analytics", label: t("nav.analytics"), icon: BarChart3, to: "/analytics", color: "#6366F1", tabletHidden: true },
    { key: "team", label: t("nav.team"), icon: Users, to: "/teams", color: "#0FA9B8" },
    // Ulture Assistant ya no vive aquí: ahora es la pestaña «Asistente» de la
    // ventana del micrófono (botón central del dock).
    { key: "settings", label: t("nav.settings"), icon: Settings, to: "/settings", color: "#64748B" },
  ];

  const filtered = items.filter((it) => {
    if (it.adminOnly && role !== "admin" && role !== "manager") return false;
    if (role === "manager") {
      const section = MANAGEABLE_SECTIONS.find((s) => it.to.startsWith(s.route));
      if (section && !isSectionVisible(section.key)) return false;
    }
    return true;
  });

  if (!open) return null;

  const handle = (it: Item) => {
    onOpenChange(false);
    navigate(it.to);
  };

  return (
    <div
      className="lg:hidden fixed inset-0 z-40 bg-slate-950/10 backdrop-blur-[2px] animate-in fade-in"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="absolute left-0 right-0"
        // Justo encima de la barra: se cuelga de las mismas variables que ella,
        // así al mover la barra el menú la sigue en vez de quedarse descolgado.
        style={{ bottom: "calc(var(--dock-bottom) + var(--dock-height) + 10px)" }}
      >
        <div className="mx-auto max-w-md md:max-w-3xl px-3 md:px-6">
          <div
            onClick={(e) => e.stopPropagation()}
            className="ml-auto w-[min(20rem,88vw)] rounded-[26px] bg-white border border-[#E4EDF5] shadow-[0_18px_50px_rgba(20,60,100,0.22)] p-2 animate-in fade-in slide-in-from-bottom-2"
          >
            {filtered.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.key}
                  onClick={() => handle(it)}
                  className={cn(
                    "w-full flex items-center gap-3.5 p-2.5 rounded-2xl hover:bg-[#F4F8FC] transition-colors text-left",
                    it.tabletHidden && "md:hidden"
                  )}
                >
                  <span
                    className="flex items-center justify-center w-12 h-12 rounded-2xl flex-shrink-0"
                    style={{
                      backgroundColor: it.color,
                      boxShadow: `0 6px 14px ${it.color}42, inset 0 1px 0 rgba(255,255,255,0.28)`,
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </span>
                  <span className="font-bold text-[15px] text-foreground leading-tight">{it.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
